import { ComponentType, createContext, useContext, useEffect, useState } from 'react';
import { IAiOfficePluginApi, IOfficeAgent, IRegisteredSlotComProps } from './api';
import { AgentShortSummary, BsUIAutomationEvent, ctxType, IAgentHelper, ICtxDetail, PreviewerActionEvent } from '@baishuyun/types';
import { ChatStatus } from 'ai';
import { PluginServiceRegistry, ServiceName } from './serviceRegistry';

// 插件级上下文，方便在插件注册的组件中获取调用 host 能力
// 使用 globalThis + Symbol.for 确保跨 bundle（主构建 & SDK 构建）共享同一个 Context 实例
const scopeKey = Symbol.for('__ai_office_plugin_scope__');
const globalCtx = (globalThis as Record<symbol, unknown>)[scopeKey] as React.Context<IAiOfficePluginApi | null> | undefined;
const PluginScopeContext: React.Context<IAiOfficePluginApi | null> =
  globalCtx || createContext<IAiOfficePluginApi | null>(null);
(globalThis as Record<symbol, unknown>)[scopeKey] = PluginScopeContext;

export const PluginScopeProvider = PluginScopeContext.Provider;


export const useUIAutoMationEvent = <K extends keyof BsUIAutomationEvent>(evtName: K, handler: (payload: BsUIAutomationEvent[K]) => void) => {
  const automationService = useService('bsUIAutomation');
  useEffect(() => {
    const off = automationService?.uiEvtBus.on(evtName, handler);

    return () => {
      if (typeof off === 'function') {
        off();
      }
    }
  }, [evtName, handler, automationService]);
}

export const useChatStatus = () => {
  const api = useHostApi();
  const [chatStatus, setChatStatus] = useState<ChatStatus>(() => api.host.chatStatus);

  useEffect(() => {
    const off = api.host.onChatStatusChange((status) => {
      setChatStatus(status);
      console.log(`[useChatStatus] chat status changed:`, status);
    });

    return () => {
      if (typeof off === 'function') {
        off();
      }
    };
  }, [api]);

  return chatStatus;
}

export const useHostApi = (): IAiOfficePluginApi => {
  const api = useContext(PluginScopeContext);
  if (!api) {
    throw new Error('useHostApi must be used within a PluginScopeContext.Provider');
  }
  return api;
};

export const useSafeHostApi = (): IAiOfficePluginApi | null => {
  const api = useContext(PluginScopeContext);
  if (!api) {
    return null;
  }

  return api;
}

export interface PluginCtxTools<T> {
  Ctx: React.Context<(() => T) | null>;
  useCtx: () => T;
  withCtx: (
    useStore: () => T,
    Component: React.ComponentType<IRegisteredSlotComProps>
  ) => ComponentType<IRegisteredSlotComProps>;
}

export const useFieldValueService = () => {
  const api = useHostApi();

  const fieldValueService = api.service.get('bsFieldValueService');

  return fieldValueService;
}


export function createPluginCtx<T>(api?: IAiOfficePluginApi): PluginCtxTools<T> {
  const Ctx = createContext<(() => T) | null>(null);

  const useCtx = (): T => {
    const useStore = useContext(Ctx);
    if (!useStore) {
      throw new Error(
        'useCtx must be used within a Plugin Context Provider. ' +
          'Make sure your component is wrapped by withCtx().'
      );
    }
    return useStore();
  };

  const withCtx = (
    useStore: () => T,
    Component: React.ComponentType<IRegisteredSlotComProps>
  ): ComponentType<IRegisteredSlotComProps> => {
    const WrappedComponent: React.FC<IRegisteredSlotComProps> = props => {
      return (
        <Ctx.Provider value={useStore}>
          <Component {...props} />
        </Ctx.Provider>
      );
    };

    if (api) {
      const scopedComp = (props: IRegisteredSlotComProps) => (
        <PluginScopeProvider value={api}>
          <WrappedComponent {...props} />
        </PluginScopeProvider>
      );

      scopedComp.displayName = `WithPluginScope(${Component.displayName || Component.name || 'Component'})`;
      return scopedComp;
    }

    WrappedComponent.displayName = `WithPluginCtx(${Component.displayName || Component.name || 'Component'})`;

    return WrappedComponent;
  };

  return { Ctx, useCtx, withCtx };
}

export * from '../app/components/chat/messages/error-boundary';

export const useService = <K extends ServiceName>(service: K): PluginServiceRegistry[K] | null => {
  const api = useSafeHostApi();
  if (!api) {
    return null;
  } 

  const s = api.service.get(service);
  return s!;
}

export const useBsHostCtx = () => {
  const api = useHostApi();
  const bsHostCtxService = api.service.get('bsHostContext');
  const [ctxDetail, setCtxDetail] = useState<ICtxDetail | null>(bsHostCtxService?.currentContext || {
    type: 'global',
    params: {},
  });

  useEffect(() => {
    if (!bsHostCtxService) {
      return;
    }

    const off = bsHostCtxService.onCtxChange((detail) => {
      setCtxDetail(detail);
    });

    return () => {
      if (typeof off === 'function') {
        off();
      }
    };
  }, [bsHostCtxService])

  return ctxDetail;
}

export const usePreviewerEvent = <K extends keyof PreviewerActionEvent>(name: K, handler: (payload: PreviewerActionEvent[K]) => void) => {
  const api = useHostApi();

  const previewerService = api.service.get('previewerService');

  useEffect(() => {
    const off = previewerService?.evtBus.on(name, handler);

    return () => {
      if (typeof off === 'function') {
        off()
      }
    }
  }, [name, handler])

}

export const useAgentsAndHelpers= (builtIn?: boolean) => {
  const { agents } = useAgents(builtIn);
  const helpers = useAgentHelpers();
  const api = useHostApi();
  const hostService = api.host;

  const helpers2agents = helpers.map(helper => {
    const a = hostService.getAgentById(helper.agentId);
    return {
      name: helper.helperName,
      id: helper.id,
      icon: a?.icon || '',
      builtIn: false,
    }
  })

  return [
    ...agents,
    ...helpers2agents
  ]
}

export const useCurrentWorkingAgent: () => { agent: AgentShortSummary; setAgent: (a:  AgentShortSummary) => void } = () => {
    const host = useHostApi().host;

    const currentWorkingAgent = useActiveAgent();

    const isHelper = currentWorkingAgent && 'agentId' in currentWorkingAgent;

    const result = !isHelper
      ? currentWorkingAgent as AgentShortSummary
      : {
        id: currentWorkingAgent.id,
        name: currentWorkingAgent.helperName,
        icon: host.getAgentById(currentWorkingAgent.agentId)?.icon || '',
        builtIn: false,
      }

    const helperService = useHostApi().service.get('agentHelperService');

    return {
      agent: result,
      setAgent: async (a) => {
        if (a.builtIn) {
          const agent = host.getAgentById(a.id);
          await host.setCurrentWorkingAgent(agent);

          return;
        }

        const helper = helperService?.getHelperById(a.id) || null;
        await host.setCurrentWorkingHelper(helper);
      }
    }
}

export const useAgents = (builtIn?: boolean) => {
  const api = useHostApi();
  const [agents, setAgents] = useState<Array<IOfficeAgent>>([]);
  const [loading, setLoading] = useState(true);
  const ctxDetail = useBsHostCtx();

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const result = await api.host.fetchAgentManifest();

      setAgents(result);
    } catch (error) {
      console.error('获取智能体列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const ctxType = ctxDetail?.type || 'global';

  useEffect(() => {
    fetchAgents();
  }, [ctxType]);

  return { agents: builtIn ? agents.filter(a => a.builtIn) : agents, loading };
};

export const useAgentHelpers = () => {
  const api = useHostApi();
  const agentHelperService = api.service.get('agentHelperService');
  const [, setVersion] = useState(0);

  const helperList = agentHelperService?.fetchAgentHelperList();

  const [helpers, setHelpers] = useState<Array<IAgentHelper>>(Array.isArray(helperList) ? helperList : []);

  useEffect(() => {
    if (!agentHelperService) {
      return;
    }

    const off = agentHelperService.onAgentHelperChange((list) => {
      setHelpers(list);
    });

    return () => {
      if (typeof off === 'function') {
        off();
      }
    };
  }, [agentHelperService]);

  useEffect(() => {
    const unsub = api.service.onRegistered('agentHelperService', () => {
      setVersion((v) => v + 1);
    });
    return unsub;
  }, [api.service]);

  return helpers;
};

export const useActiveAgentName = () => {
  const a = useActiveAgent();

  if (!a) {
    return "";
  }

  const helper = a as IAgentHelper;
  if (helper.helperName) {
    return helper.helperName;
  }

  const agent = a as IOfficeAgent;

  return agent.name;
}

export const useActiveAgent = (): IOfficeAgent | IAgentHelper | null => {
  const api = useHostApi();
  const [activeAgent, setActiveAgent] = useState<IOfficeAgent | IAgentHelper | null>(
    () => api.host.currentWorkingAgent || api.host.currentWorkingHelper
  );

  useEffect(() => {
    const unsubscribe = api.host.onActiveAgentChange((agent) => {
      if (!agent) {
        return;
      }

      setActiveAgent(agent);
    });

    const unsubscribeHelper = api.host.onActiveHelperChange((helper) => {
      if (!helper) {
        return;
      }

      setActiveAgent(helper);
    });

    return () => {
      unsubscribe();
      unsubscribeHelper();
    };
  }, [api]);

  return activeAgent;
};

export const withStyle = (Component: React.ComponentType<IRegisteredSlotComProps>, style?: string) => {
  if (!style) {
    return Component;
  }

  return (props: IRegisteredSlotComProps) => (
    <>
      <style>{style}</style>
      <Component {...props} />
    </>
  );
};